import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Upgrade } from '@mui/icons-material';
import { ConfigSchema, inspectConfigFile, migrateConfigJson } from '../schema/schema';
import { useI18n } from '../i18n';

type Props = {
  schema: ConfigSchema;
  raw: string;
  disabled?: boolean;
  onMigrated: (next: string) => void;
};

const ConfigMigrateBanner: React.FC<Props> = ({ schema, raw, disabled, onMigrated }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const info = inspectConfigFile(raw, schema);

  if (info.valid && info.tooNew) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {t('migrate.tooNew', { version: info.version, target: info.targetVersion })}
      </Alert>
    );
  }

  if (!info.valid || !info.needsMigration) {
    return null;
  }

  const handleMigrate = () => {
    try {
      setError(null);
      onMigrated(migrateConfigJson(schema, raw));
      setOpen(false);
    } catch (err: any) {
      setError(err?.message || t('migrate.failed'));
    }
  };

  return (
    <>
      <Alert
        severity="warning"
        sx={{ mb: 2 }}
        action={(
          <Button
            color="inherit"
            size="small"
            startIcon={<Upgrade />}
            disabled={disabled}
            onClick={() => setOpen(true)}
          >
            {t('migrate.button', { version: info.targetVersion })}
          </Button>
        )}
      >
        {t('migrate.banner', {
          version: info.version ?? 1,
          flat: info.flat ? t('migrate.flatKeys') : '',
          target: info.targetVersion,
        })}
      </Alert>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('migrate.title', { version: info.targetVersion })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('migrate.body1')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('migrate.body2', { version: info.targetVersion })}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleMigrate} disabled={disabled}>
            {t('migrate.rewrite')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConfigMigrateBanner;
